import { useMutation } from "@/api/utils/useQuery"
import { MutationResult } from "@/api/utils/types"
import {
  DeletePaymentReviewRecordsRequest,
  DeletePaymentReviewRecordsResponse,
  DownloadPaymentReviewsRequest,
  DownloadPaymentReviewsResponse,
  PaymentReviewRetryResponse,
  PaymentReviewsRetryRequest,
  PaymentReviewsRetryResponse,
} from "@/api/backofficeRest/generated/model"
import {
  BackofficeRestMutations,
  RetryPaymentReviewsByFiltersPayload,
} from "@/api/backofficeRest/mutations"
import {
  IDeletePaymentReviewRecordsByIds,
  IDownloadPaymentReviewRecords,
  IRetryPaymentReviewRecordsByFilters,
  IRetryPaymentReviewRecordsByIds,
} from "./PaymentReviewMutator.types"
import { serializeFilters } from "./PaymentReviewMutator.parser"

export interface IPaymentReviewMutator {
  retryRecordsByIds(): MutationResult<
    IRetryPaymentReviewRecordsByIds,
    PaymentReviewsRetryResponse
  >
  retryRecordsByFilters(): MutationResult<
    IRetryPaymentReviewRecordsByFilters,
    PaymentReviewRetryResponse
  >
  deleteRecordsByIds(): MutationResult<
    IDeletePaymentReviewRecordsByIds,
    DeletePaymentReviewRecordsResponse
  >
  downloadRecords(): MutationResult<
    IDownloadPaymentReviewRecords,
    DownloadPaymentReviewsResponse
  >
}

class PaymentReviewMutatorClass implements IPaymentReviewMutator {
  retryRecordsByIds() {
    return useMutation({
      ...BackofficeRestMutations.paymentReviews.retryRecordsByIds(),
      invalidateCache: false,
      serializerFn: (
        data: IRetryPaymentReviewRecordsByIds,
      ): PaymentReviewsRetryRequest => ({
        ids: data.ids,
        enableDelay: true,
        overrideIncreaseThreshold: data.overrideIncreaseThreshold,
        ignoreAccountBalance: data.ignoreAccountBalance,
      }),
    })
  }

  retryRecordsByFilters() {
    return useMutation({
      ...BackofficeRestMutations.paymentReviews.retryRecordsByFilters(),
      invalidateCache: false,
      serializerFn: (
        data: IRetryPaymentReviewRecordsByFilters,
      ): RetryPaymentReviewsByFiltersPayload => ({
        paymentReviewId: data.paymentReviewId,
        body: {
          enableDelay: true,
          overrideIncreaseThreshold: data.overrideIncreaseThreshold,
          ignoreAccountBalance: data.ignoreAccountBalance,
          filters: serializeFilters(data.paymentReviewId, data.filters),
        },
      }),
    })
  }

  deleteRecordsByIds() {
    return useMutation({
      ...BackofficeRestMutations.paymentReviews.deleteRecordsByIds(),
      invalidateCache: false,
      serializerFn: (
        data: IDeletePaymentReviewRecordsByIds,
      ): DeletePaymentReviewRecordsRequest => ({ ids: data.ids }),
    })
  }

  downloadRecords() {
    return useMutation({
      ...BackofficeRestMutations.paymentReviews.downloadRecords(),
      invalidateCache: false,
      serializerFn: (
        data: IDownloadPaymentReviewRecords,
      ): DownloadPaymentReviewsRequest => ({
        filters: serializeFilters(data.paymentReviewId, data.filters),
      }),
    })
  }
}

export const PaymentReviewMutator = new PaymentReviewMutatorClass()
